import React, {useCallback, useReducer} from "react"
import {Button, Modal} from "react-bootstrap"
import {FormattedMessage} from "react-intl"
import ActionButton from "../button/actionbutton.component"

const HIDE = "hide"
type HIDE = typeof HIDE

const CONFIRM = "confirm"
type CONFIRM = typeof CONFIRM


const VALIDATE = "validate"
type VALIDATE = typeof VALIDATE

const TOGGLE = "toggle"
type TOGGLE = typeof TOGGLE

const UPDATE = "update"
type UPDATE = typeof UPDATE

const SET_DISABLED = "set-disable"
type SET_DISABLED = typeof SET_DISABLED

const SET_LOADING = "set-loading"
type SET_LOADING = typeof SET_LOADING

type Action =
    | { type: HIDE | CONFIRM | VALIDATE | TOGGLE }
    | { type: UPDATE, payload: Partial<Settings> }
    | { type: SET_DISABLED, payload: boolean }
    | { type: SET_LOADING, payload: boolean }

type Settings = {
    show: boolean,
    header: string,
    centered: boolean,
    body: JSX.Element | JSX.Element[],
    bodyStyle: React.CSSProperties
    confirmBtnText: JSX.Element | string,
    modalId: string,
    size: "sm" | "lg" | "xl" | undefined, //Medium size is undefined but it's the default modal size
    showFooter: boolean,
    showConfirmBtn: boolean,
    confirmBtnDisabled: boolean,
    confirmBtnLoading: boolean,
    confirmBtnType?: "button" | "submit" | "reset",
    confirmBtnClassnames?: string,
    confirmBtnFormId?: string,
    hasConfirmed: boolean,
    closeBtnText: JSX.Element,
    hasClosed: boolean,
}

const DefaultSettings: Settings = {
    show: false,
    header: "",
    centered: false,
    body: <></>,
    bodyStyle: {},
    modalId: "",
    size: undefined,
    showFooter: true,
    showConfirmBtn: true,
    confirmBtnText: <FormattedMessage id={"common.hooks.useModal.confirm_btn"} defaultMessage={"Start"}/>,
    confirmBtnDisabled: false,
    confirmBtnLoading: false,
    confirmBtnType: "button",
    confirmBtnClassnames: "btn btn-primary",
    confirmBtnFormId: "",
    hasConfirmed: false,
    closeBtnText: <FormattedMessage id={"common.hooks.useModal.close_btn"} defaultMessage={"Close"}/>,
    hasClosed: false,
}

type ParamSettings = Omit<Settings, "hasClosed" | "hasConfirmed">

const SettingsReducer = (state: Settings, action: Action): Settings => {
    switch (action.type) {
    case HIDE:
        return {
            ...state,
            show: false,
            hasClosed: true
        }
    case CONFIRM:
        return {
            ...state,
            show: false,
            hasClosed: true,
            hasConfirmed: true,
        }
    case VALIDATE:
        return {
            ...state,
            hasConfirmed: true,
        }
    case TOGGLE:
        return {
            ...state,
            show: !state.show,
            hasClosed: state.show,
            hasConfirmed: false,
        }
    case UPDATE:
        return {
            ...state,
            ...action.payload,
        }
    case SET_DISABLED:
        return {
            ...state,
            confirmBtnDisabled: action.payload
        }
    case SET_LOADING:
        return {
            ...state,
            confirmBtnLoading: action.payload
        }
    default:
        return state
    }
}

const UseModal = (
    initSettings: Partial<ParamSettings>,
) => {
    const [state, dispatch] = useReducer(SettingsReducer, {
        ...DefaultSettings,
        ...initSettings,
    })

    const onHide = (): void => {
        dispatch({
            type: HIDE
        })
    }

    const onConfirm = (): void => {
        dispatch({
            type: CONFIRM
        })
    }

    const onValidate = (): void => {
        dispatch({
            type: VALIDATE
        })
    }

    const onToggle = useCallback(() => {
        dispatch({
            type: TOGGLE
        })
    }, [])

    const updateSettings = useCallback((settings: Partial<Settings>) => {
        dispatch({
            type: UPDATE,
            payload: settings,
        })
    }, [])

    const setDisabled = (disabled: boolean) => {
        if (disabled === state.confirmBtnDisabled) return
        dispatch({
            type: SET_DISABLED,
            payload: disabled
        })
    }

    const setLoading = (loading: boolean) => {
        if (loading === state.confirmBtnLoading) return
        dispatch({
            type: SET_LOADING,
            payload: loading
        })
    }

    const modal = (
        <Modal
            id={state.modalId}
            className={"custom-modal"}
            show={state.show}
            onHide={() => onHide()}
            scrollable={true}
            size={state.size}
            backdrop="static"
            centered={state.centered}
        >

            {state.header &&
                <Modal.Header closeButton>
                    {state.header}
                </Modal.Header>
            }

            <Modal.Body style={state.bodyStyle}>
                {state.body}
            </Modal.Body>

            {state.showFooter &&
                <Modal.Footer>
                    <Button variant={"secondary"} onClick={() => onHide()}>
                        {state.closeBtnText}
                    </Button>

                    <ActionButton
                        type={state.confirmBtnType}
                        formId={state.confirmBtnFormId || ""}
                        className={state.confirmBtnClassnames || ""}
                        disabled={state.confirmBtnDisabled}
                        isLoading={state.confirmBtnLoading}
                        onClick={() => {
                            if (state.confirmBtnType !== "submit") {
                                onConfirm()
                            } else {
                                onValidate()
                            }
                        }}
                    >
                        {state.confirmBtnText}
                    </ActionButton>
                </Modal.Footer>
            }
        </Modal>
    )


    return {
        modal: modal,
        setContent: updateSettings,
        toggleShow: onToggle,
        hasClosed: state.hasClosed,
        hasConfirmed: state.hasConfirmed,
        setSubmitDisabled: setDisabled,
        setLoading: setLoading,
    }
}

export default UseModal


// WEBPACK FOOTER //
// ./src/common/hooks/UseModal.tsx